<template>
  <div
    :class="[
      {
        Blurred: selectedQuestion.question_uuid && !showReplaceQuestionView,
        ReplaceQuestionWrapper: showReplaceQuestionView,
      },
      formType,
      'QuestionsList-container',
    ]"
  >
    <div class="Search-container">
      <div
        v-if="showReplaceQuestionView"
        class="ReplaceQuestion"
      >
        <h2>
          Replace
          <span class="Question">{{
            questionToReplace.from.question_uuid
          }}</span>
          with
        </h2>
      </div>
      <rmp-input
        v-model.trim="searchQuery"
        placeholder="Search"
        :clearable="true"
        :has-icon="true"
        icon="Icon Icon-search"
      />
    </div>
    <hr>
    <div class="QuestionBuilderList-container">
      <div
        v-for="(category, index) in questionCategoryHeaders"
        :key="`header_${index}`"
      >
        <div class="accordion">
          <fieldset
            v-if="getQuestionsByCategory(category).length > 0"
            class="Fieldset-collapsible active"
          >
            <legend @click="toggleCollapse($event)">
              {{ category }}
            </legend>
            <draggable
              :list="getQuestionsByCategory(category)"
              class="list-group collapsed"
              group="questions"
              :clone="handleDragEvent"
              @start="handleDragQuestion"
              @end="handleDragQuestion"
            >
              <div
                v-for="question in getQuestionsByCategory(category)"
                :key="`${question.question_uuid}-${index}`"
                class="list-group-item u-flex NewQuestion"
                :class="{ disabled: isSingleUseQuestion(question) }"
                @click="$emit('question-list:select-question', question)"
              >
                <span
                  :class="question.icon"
                />
                <div class="list-group-item-details">
                  <h3>
                    {{ question.label }}
                  </h3>
                  <span class="Hint">
                    {{ question.type }}
                  </span>
                </div>
              </div>
            </draggable>
          </fieldset>
        </div>
      </div>
    </div>
    <button
      v-if="showReplaceQuestionView"
      class="Button Button--green-outline"
      @click="$emit('question-list:cancel-replace-question')"
    >
      Cancel
    </button>
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex';
import RmpInput from '@rmpenterprise/form-input';
import Draggable from 'vuedraggable';
import { nanoid } from 'nanoid';

export default {
  components: { Draggable, RmpInput },

  props: {
    questionToReplace: {
      type: Object,
      default() {
        return {
          from: null,
          to: null,
        };
      },
    },
  },
  data() {
    return {
      searchQuery: '',
    };
  },
  computed: {
    ...mapState('events', {
      questions: (state) => state.lists.questions,
      event: (state) => state.event,
    }),
    ...mapState('forms', {
      selectedQuestion: (state) => state.selectedQuestion,
      formQuestions: (state) => state.form.questions,
      formType: (state) => state.form.type,
    }),

    ...mapGetters('forms', ['currentDynamicFormScreen', 'dynamicFormQuestionList']),

    showReplaceQuestionView() {
      return this.questionToReplace.from !== null;
    },

    questionList() {
      if (this.searchQuery !== '' || this.questionToReplace.from !== null) {
        this.expandAllAccordionSections();
      } else {
        this.expandAllAccordionSections(false);
      }
      // Replace question should return a filtered list of available questions based on list_of_questions_that_can_replace
      if (this.questionToReplace.from !== null) {
        let { selectedQuestion } = this;
        if (this.event.type === 'dynamic') {
          selectedQuestion = this.dynamicFormQuestionList[this.currentDynamicFormScreen.questionVuexId];
        }
        return this.questions.filter((q) => {
          return selectedQuestion.list_of_questions_that_can_replace.indexOf(q.question_uuid) !== -1 && !this.isSingleUseQuestion(q);
        });
      }
      // List of questions optionally filtered by search term

      const filteredQuestions = this.getFilteredQuestionsList;

      return [...filteredQuestions];
    },

    getFilteredQuestionsList() {
      if (this.searchQuery === '') return this.questions;

      return this.questions.filter((q) => {
        const questionLabel = q.label.toLowerCase();
        const searchTerm = this.searchQuery.toLowerCase();

        return questionLabel.indexOf(searchTerm) > -1;
      });
    },

    questionCategoryHeaders() {
      // Custom questions have no sub_catgory. Add category header instead
      const headers = this.questionList.map(
        (q) => q.sub_category || q.category,
      );
      return new Set(headers);
    },

    formQuestionUuids() {
      return this.formQuestions.map((question) => question.question_uuid);
    },
  },
  methods: {
    getQuestionsByCategory(categoryName) {
      return this.questionList.filter(
        (q) => q.is_available_for_web_form_of_types.includes(this.formType) && (q.sub_category === categoryName || q.category === categoryName),
      );
    },

    isSingleUseQuestion(question) {
      // Certain questions can only be added to a form once
      return (
        !question.is_custom_question
        && this.formQuestionUuids.includes(question.question_uuid)
        && !['header', 'paragraph', 'statement'].includes(question.type)
      );
    },

    handleDragQuestion(ev) {
      if (this.formType === 'static' && this.formQuestions.length === 0) {
        if (ev.type === 'start') {
          document
            .querySelector('.NoQuestionsPlaceholder')
            .classList.add('DragStart');
        } else {
          document
            .querySelector('.NoQuestionsPlaceholder')
            .classList.remove('DragStart');
        }
      }
    },

    handleDragEvent(question) {
      // To add multiple custom questions of the same type ie autocomplete, checkbox, date etc
      // A unique index must be added each input option
      // Since uuids will not be unique for each duplicate form field

      return {
        ...question,
        index: this.formQuestions.length,
        vuexId: nanoid(),
      };
    },

    expandAllAccordionSections(expand = true) {
      document.querySelectorAll('.Fieldset-collapsible').forEach((item) => {
        if (expand) {
          item.classList.remove('active');
          item.querySelector('.list-group').classList.remove('collapsed');
        } else {
          item.classList.add('active');
          item.querySelector('.list-group').classList.add('collapsed');
        }
      });
    },

    toggleCollapse(ev) {
      const fieldset = ev.target.parentElement;
      const body = fieldset.querySelector('.list-group');

      fieldset.classList.toggle('active');
      body.classList.toggle('collapsed');
    },
  },
};
</script>

<style lang="less" scoped>
.Blurred {
  filter: opacity(0.1);
  pointer-events: none;
}

.NewQuestion.list-group-item {
  margin: 5px 0;

  &.sortable-ghost {
    box-shadow: none;
    padding: 5px;
    border-radius: 5px;
    border: 2px dotted #046ff0;
    background: transparent;
  }

  .Icon-s {
    background: #e0e4ea;
    font-size: 17px;
    border: 1px solid #d0d7e1;
    padding: 9px 12px;
    align-self: baseline;
    border-radius: 10px;
    margin-right: 10px;
  }

  .list-group-item-details {
    h3 {
      margin: 0;
    }
  }
}

.ReplaceQuestionWrapper {
  position: fixed;
  width: 100%;;
  max-width: 439px;
  padding: 25px;
  top: 0;
  z-index: 2;
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  background-color: #ffffff;
  box-shadow: 0 13px 29px 0 rgba(0, 0, 0, 0.11);

  .ReplaceQuestion {
    .Question {
      background: #e0e4ea;
      padding: 2px 6px;
      border-radius: 3px;
    }
  }
}

.QuestionsList-container {
  position: sticky;
  top: 60px;

  hr {
    margin: 0;
  }

  .QuestionBuilderList-container {
    height: 80vh;
    overflow-y: auto;
    background: white;
    padding: 20px 20px 40px 20px;

    legend {
      background: none;
      font-weight: bold;

      &:after {
        color: black;
      }
    }
  }

  .sortable-ghost,
  .sortable-chosen {
    padding: 5px;
  }

  .sortable-chosen {
    border: 1px solid #046ff0;
    border-radius: 6px;
    background-color: #e1edfa;
  }

  .list-group {
    display: flex;
    flex-wrap: wrap;
    padding: 10px;

    &.collapsed {
      display: none;
    }
    .list-group-item {
      width: 200px;
      cursor: move;

      &.disabled {
        color: lightgray;
        pointer-events: none;

        h3,
        .Hint {
          color: lightgray;
        }
      }
    }
  }
}

// Dynamic form specific styles
.QuestionsList-container.dynamic {
  position: static;
  .QuestionBuilderList-container {
    height: auto;
  }
}

</style>
